import "./index.css";
import Group from "./Group";
import { Modal } from "antd";
import DragLayout from "./DragLayout";
import { request } from "../../../../request";
import React, { memo, useMemo, useState } from "react";
import { CloseOutlined, SettingOutlined } from "@ant-design/icons";

import type { TypeGuidelineSelectProps } from "..";
import type { TypeDragLayoutProps } from "./DragLayout";
import type { TypeTag, TypeGuideline } from "MortgageGPT/hooks/useAllPrograms";

interface TypeTagManageProps
  extends Pick<TypeGuidelineSelectProps, "programs"> {
  /**
   * @name onDeleteTag 删除tag后
   * @param id 被删除的tag id
   * @description 如果“tag”被删除掉，之前的 select tag 也该被清理掉
   */
  onDeleteTag(id: TypeTag["id"]): void;
}

/**
 * @name TypeTagFilterItem tags需要显示旗下关联的 ”guidelines“，这里自行过滤增加了 ”guidelines“字段
 */
export interface TypeTagFilterItem extends TypeTag {
  /**
   * @param guidelines 这个是转换、分组后的 “guidelines”
   */
  guidelines: Array<
    Record<"id" | "lenderId" | "lenderName" | "lenderDisplayName", string> & {
      options: TypeGuideline[];
    }
  >;
}

/**
 * @name TagManage 管理 Tag 关联的 “Guideline”
 */
const TagManage: React.FC<TypeTagManageProps> = ({ programs, onDeleteTag }) => {
  const { TAGS, MAP_GUIDELINE_ID } = programs.data;

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const data = useMemo(() => {
    if (!TAGS?.length) return [];
    return TAGS.map((v) => {
      const guidelines = {};
      for (const k of v.documents) {
        const val = MAP_GUIDELINE_ID[k];
        if (!val) continue;
        if (guidelines[val.lenderId]) {
          guidelines[val.lenderId].options.push(val);
        } else {
          guidelines[val.lenderId] = {
            options: [val],
            lenderId: val.lenderId,
            lenderName: val.lenderName,
            lenderDisplayName: val.lenderDisplayName,
          };
        }
      }
      return { ...v, guidelines: Object.values(guidelines) };
    }) as TypeTagFilterItem[];
  }, [TAGS, MAP_GUIDELINE_ID]);

  const onSortChange: TypeDragLayoutProps["onChange"] = async (value) => {
    try {
      setLoading(true);
      programs.mutate({ ...programs.data, TAGS: value });
      await request(`/api/v1/mortgagegpt/updateSessionConfig`, {
        body: { tagsOrder: value.map((v) => v.id) },
        method: "PUT",
      });
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  async function onNameChange(body: TypeTagFilterItem) {
    await request(`/api/v1/mortgagegpt/updateSessionTag`, {
      body,
      method: "PUT",
    });
    programs.run();
  }

  function onDelete(value: TypeTagFilterItem) {
    const confirm = Modal.confirm({
      title: (
        <>
          <span>Confirm delete</span>
          <CloseOutlined onClick={() => confirm.destroy()} rev={undefined} />
        </>
      ),
      className: "modal-delete",
      content: "Are you sure you want to delete?",
      okText: "Delete",
      async onOk() {
        await request(`/api/v1/mortgagegpt/deleteSessionTag`, {
          body: { id: value.id },
          method: "DELETE",
        });
        onDeleteTag(value.id);
        programs.run();
      },
    });
  }

  function onOpen() {
    setOpen((b) => !b);
  }

  return (
    <>
      <span onClick={onOpen} className="guideline-manage-btn">
        <SettingOutlined rev={undefined} />
        <span>Manage</span>
      </span>

      <Modal
        centered
        open={open}
        width={566}
        footer={null}
        destroyOnClose
        onCancel={onOpen}
        title="Manage selections"
        className="guideline-manage guideline-modal"
      >
        <DragLayout loading={loading} value={data} onChange={onSortChange}>
          {data?.map((v) => (
            <Group
              data={v}
              key={v.id}
              tags={data}
              onDelete={onDelete}
              onSave={onNameChange}
            >
              {v.guidelines.map((v) => (
                <Group.Item
                  noEditing
                  key={v.lenderId}
                  options={v.options}
                  name={v.lenderDisplayName}
                  count={v.options?.length || 0}
                />
              ))}
            </Group>
          ))}
        </DragLayout>
      </Modal>
    </>
  );
};

export default memo(TagManage);
